import { getErrorMessage } from '../../utils/serviceUtils';
import { randomId } from '../../utils/randomId';
import React from 'react';
import { BotProfile, GameState, Player, AddPlayerGameEvent, PlayerLeftGameEvent } from '../../types/game';
import { initialState } from '../gameReducer';
import { eventStore } from '../../services/EventStore';
import { generateGameId } from '../../utils/gameId';
import { nextGroupGameId, parseGroupGameId } from '../../utils/groupGameId';
import { perf } from '../../utils/perfMetrics';
import { clearReservedPlayerId, clearSessionPlayerId, getReservedPlayerId, getSessionPlayerId, setReservedPlayerId, setSessionPlayerId } from '../../utils/sessionPlayer';
import { clearGameState, clearPersistedEventCaches, clearPerGameKeys, GAME_ID_KEY, clearAllDiceKeys } from '../../utils/gamePersistence';
import { GameAction } from '../gameReducer';
import { debugLog } from '../debug/debugLog';
import { dispatchApplyEvent } from '../debug/applyEventTrace';
import { logger } from '../../utils/logger';
import { hardResetStorage, writeSession, clearSession } from '../../utils/sessionContract';
import { makeGameCreatedEvent, makeGameRestartedEvent, makePenaltyEvent } from '../../domain/events';
import { placeZones, type TrackZone } from '../../domain/zones';
import { loadGroupToken, rememberGroupContext, rememberGroupPlayer } from '../../utils/groupPersistence';
import { PLAYER_MAX_COUNT } from '../../utils/constants';
import { requestLobbyState, sendOnlineCommand } from '../../services/transport/onlineTransportMirror';
import { attachGameToGroup, findGroupByGame } from '../../services/GroupService';
import { resolveOnlineIdentity } from '../../utils/onlineIdentity';
import { normalizeNextGameParticipation } from '../../domain/nextGameParticipation';
import { pl } from '../../i18n/locales/pl';
import { en } from '../../i18n/locales/en';

/**
 * Podstawiona mapa stref dla E2E. Poza produkcją, tym samym trybem co `__MOCK_DICE_QUEUE__`.
 */
function readMockZones(): TrackZone[] | null {
  if (typeof window === 'undefined') return null;
  if (import.meta.env.PROD) return null;
  const mocked = window.__MOCK_ZONES__;
  if (!Array.isArray(mocked) || mocked.length === 0) return null;
  return mocked.map((zone) => ({ ...zone, revealed: false }));
}

interface LastScoreAction {
  playerId: string;
  points: number;
  turn: number;
  time: number;
}

interface UseGameActionsParams {
  gameState: GameState;
  dispatch: React.Dispatch<GameAction>;
  setIsHost: (value: boolean) => void;
  setCurrentClientPlayerId: (value: string | null) => void;
  modeHint?: GameState['mode'];
}

export interface GameActions {
  addPlayer: (name: string, options?: { gameId?: string; groupToken?: string | null }) => Promise<boolean>;
  addPlayerRemote: (name: string, gameId?: string, options?: { role?: 'player' | 'viewer'; groupToken?: string | null }) => Promise<boolean>;
  addOnlinePlayer: (name: string, options?: { gameId?: string; groupToken?: string | null }) => Promise<boolean>;
  joinOnlineSelf: (name: string, options?: { gameId?: string; role?: 'player' | 'viewer'; groupToken?: string | null; assumeHost?: boolean }) => Promise<boolean>;
  addBotPlayers: (count: number, profile: BotProfile) => Promise<boolean>;
  removeBotPlayer: (botId: string) => Promise<boolean>;
  markSelfPlayer: (playerId: string | null) => void;
  reorderPlayers: (players: Player[]) => void;
  startGame: (id?: string) => void;
  startNextGame: () => Promise<string | null>;
  addScore: (playerId: string, points: number) => void;
  undoLastAction: () => void;
  resetGame: () => void;
  exitGame: () => void;
  joinGame: (gameId: string, options?: { groupToken?: string | null; skipGroupLookup?: boolean; assumeHost?: boolean }) => Promise<boolean>;
  leaveGame: () => Promise<boolean>;
}

const debug = (label: string, payload: unknown) => debugLog(label, payload, { source: 'useGameActions' });

function readUiLanguage(): 'pl' | 'en' {
  if (typeof window === 'undefined') return 'pl';
  try {
    const stored = window.localStorage?.getItem('dice1k-language');
    return stored === 'en' ? 'en' : 'pl';
  } catch {
    return 'pl';
  }
}

const BOT_PROFILE_LABELS: Record<'pl' | 'en', Record<BotProfile, string>> = {
  pl: {
    conservative: pl['online.bots.variant.conservative'],
    balanced: pl['online.bots.variant.balanced'],
    risky: pl['online.bots.variant.risky'],
  },
  en: {
    conservative: en['online.bots.variant.conservative'],
    balanced: en['online.bots.variant.balanced'],
    risky: en['online.bots.variant.risky'],
  },
};

function botProfileLabel(profile: BotProfile, language: 'pl' | 'en'): string {
  return BOT_PROFILE_LABELS[language][profile];
}

export function useGameActions({ gameState, dispatch, setIsHost, setCurrentClientPlayerId, modeHint }: UseGameActionsParams): GameActions {
  const lastScoreActionRef = React.useRef<LastScoreAction | null>(null);
  const addOnlinePlayer = React.useCallback(async (name: string, options?: { gameId?: string; groupToken?: string | null }) => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    const targetId = options?.gameId || gameState.id;
    if (!targetId) {
      logger.warn('USE_GAME_ACTIONS_ADD_ONLINE_PLAYER_MISSING_ID');
      return false;
    }
    const eventId = randomId();
    const commandPlayerId = randomId();
    const ok = await sendOnlineCommand(targetId, {
      type: 'UPSERT_PLAYER',
      eventId,
      playerId: commandPlayerId,
      name: trimmed,
      role: 'player',
      isHost: false,
    }, {
      playerId: getSessionPlayerId(),
      playerName: null,
      role: null,
    });
    if (!ok) {
      logger.error('USE_GAME_ACTIONS_ADD_ONLINE_PLAYER_FAILED', {
        gameId: targetId,
        eventId,
        playerId: commandPlayerId,
      });
      return false;
    }
    if (!gameState.id) {
      dispatch({ type: 'SET_GAME_ID', id: targetId });
    }
    return true;
  }, [dispatch, gameState.id]);

  const joinOnlineSelf = React.useCallback(async (
    name: string,
    options?: { gameId?: string; role?: 'player' | 'viewer'; groupToken?: string | null; assumeHost?: boolean },
  ) => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    const targetId = (options?.gameId ? options.gameId.toUpperCase() : gameState.id);
    if (!targetId) return false;
    const groupToken = options?.groupToken ?? gameState.groupToken ?? null;

    const sameGame = gameState.id && targetId && gameState.id.toUpperCase() === targetId.toUpperCase();
    let targetState: GameState | null;
    if (sameGame) {
      targetState = gameState;
    } else {
      try {
        targetState = await eventStore.getGameState(targetId);
      } catch {
        targetState = null;
      }
    }

    const inProgress = (targetState?.lobby?.phase === 'IN_GAME') || Boolean(targetState?.turnCycle);
    const desiredRole: 'player' | 'viewer' = options?.role || (inProgress ? 'viewer' : 'player');
    const playerCount = targetState?.players?.length ?? gameState.players.length;
    if (desiredRole === 'player' && !inProgress && playerCount >= PLAYER_MAX_COUNT) throw new Error('GAME_FULL');

    const sessionPlayerId = getSessionPlayerId();
    const reservedPlayerId = getReservedPlayerId(targetId);
    const hasActiveSessionPlayer = Boolean(
      sessionPlayerId
      && targetState?.players?.some((player) => player.id === sessionPlayerId && (player.status || 'active') === 'active')
    );
    const hasActiveSessionSpectator = Boolean(
      sessionPlayerId
      && targetState?.spectators?.some((spectator) => spectator.id === sessionPlayerId)
    );
    const shouldReuseSessionIdentity = desiredRole === 'player'
      ? hasActiveSessionPlayer
      : hasActiveSessionSpectator;
    const actorId = (sessionPlayerId && shouldReuseSessionIdentity)
      ? sessionPlayerId
      : (reservedPlayerId || randomId());
    const eventId = randomId();
    const commandOk = await sendOnlineCommand(targetId, {
      type: 'UPSERT_PLAYER',
      eventId,
      playerId: actorId,
      name: trimmed,
      role: desiredRole,
      ...(desiredRole === 'player'
        ? { isHost: options?.assumeHost === true }
        : { isHost: false }),
    }, {
      playerId: actorId,
      playerName: trimmed,
      role: desiredRole,
    });
    if (!commandOk) {
      logger.error('USE_GAME_ACTIONS_JOIN_SELF_COMMAND_FAILED', {
        gameId: targetId,
        eventId,
        playerId: actorId,
        role: desiredRole,
      });
      return false;
    }

    const shouldAssumeHost = desiredRole === 'player'
      ? (
          typeof options?.assumeHost === 'boolean'
            ? options.assumeHost
            : Boolean(
              targetState?.players?.find((player) => player.id === actorId && (player.status || 'active') === 'active')?.isHost,
            )
        )
      : false;
    setCurrentClientPlayerId(actorId);
    setSessionPlayerId(actorId);
    clearReservedPlayerId(targetId);
    setIsHost(shouldAssumeHost);
    rememberGroupPlayer(groupToken, actorId, { isHost: shouldAssumeHost });
    try { localStorage.setItem('dice1k_isHost', shouldAssumeHost ? 'true' : 'false'); } catch {}
    try { localStorage.setItem(`dice1k_last_player_id_${targetId}`, actorId); } catch {}
    return true;
  }, [gameState, setCurrentClientPlayerId, setIsHost]);

  const addPlayer = React.useCallback(async (name: string, options?: { gameId?: string; groupToken?: string | null }) => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    const shouldUseOnlinePath = gameState.mode === 'ONLINE' || modeHint === 'ONLINE' || Boolean(options?.gameId);
    if (shouldUseOnlinePath) {
      return addOnlinePlayer(trimmed, options);
    }
    let targetId = gameState.id;
    let createdNewRealGame = false;
    if (!targetId) {
      targetId = generateGameId();
      dispatch({ type: 'SET_GAME_ID', id: targetId });
      createdNewRealGame = true;
    }
    if (createdNewRealGame) {
      const createdEvent = makeGameCreatedEvent('REAL');
      dispatchApplyEvent(dispatch, createdEvent, 'local');
      void eventStore.appendEvent(targetId, createdEvent)
        .then((createdResult) => {
          if (!createdResult.success) {
            logger.error('USE_GAME_ACTIONS_APPEND_GAME_CREATED_REAL_FAILED', {
              gameId: targetId,
              eventId: createdEvent.id,
              error: createdResult.error,
              warning: createdResult.warning,
            });
          }
        })
        .catch((e) => {
          logger.error('USE_GAME_ACTIONS_APPEND_GAME_CREATED_REAL_FAILED', e);
        });
    }
    const playerId = randomId();
    const eventId = randomId();
    const event: AddPlayerGameEvent = {
      id: eventId,
      type: 'ADD_PLAYER',
      turn: 0,
      timestamp: new Date().toISOString(),
      payload: { playerId, name: trimmed, eventId },
    };
    dispatchApplyEvent(dispatch, event, 'local');
    try {
      const result = await eventStore.appendEvent(targetId, event);
      if (!result.success) {
        logger.error('USE_GAME_ACTIONS_APPEND_PLAYER_REAL_FAILED', {
          gameId: targetId,
          eventId: event.id,
          error: result.error,
          warning: result.warning,
        });
        return false;
      }
    } catch (e) {
      logger.error('USE_GAME_ACTIONS_APPEND_PLAYER_REAL_FAILED', e);
      return false;
    }
    return true;
  }, [addOnlinePlayer, dispatch, gameState.id, gameState.mode, modeHint]);

  const addPlayerRemote = React.useCallback(async (name: string, gameId?: string, options?: { role?: 'player' | 'viewer'; groupToken?: string | null }) => {
    return joinOnlineSelf(name, {
      gameId,
      role: options?.role,
      groupToken: options?.groupToken,
      assumeHost: false,
    });
  }, [joinOnlineSelf]);

  const addBotPlayers = React.useCallback(async (count: number, profile: BotProfile): Promise<boolean> => {
    if (gameState.mode !== 'ONLINE' || !gameState.id) return false;
    if (!Number.isFinite(count) || count <= 0) return false;
    if (gameState.lobby?.phase !== 'LOBBY') return false;
    const hostId = getSessionPlayerId();
    if (!hostId) return false;

    const activePlayers = gameState.players.filter((player) => (player.status || 'active') === 'active' && (player.role || 'player') === 'player');
    const availableSlots = Math.max(0, PLAYER_MAX_COUNT - activePlayers.length);
    const toAdd = Math.min(Math.floor(count), availableSlots);
    if (toAdd <= 0) return false;

    const normalizedExistingNames = new Set(gameState.players.map((player) => player.name.trim().toLowerCase()));
    const language = readUiLanguage();
    const profileLabel = botProfileLabel(profile, language);
    let created = 0;

    for (let index = 0; index < toAdd; index += 1) {
      let suffix = 1;
      let proposedName = profileLabel;
      while (normalizedExistingNames.has(proposedName.toLowerCase())) {
        suffix += 1;
        proposedName = `${profileLabel}-${suffix}`;
      }
      normalizedExistingNames.add(proposedName.toLowerCase());

      const ok = await sendOnlineCommand(gameState.id, {
        type: 'ADD_BOT',
        eventId: randomId(),
        hostId,
        botId: randomId(),
        name: proposedName,
        profile,
      });
      if (!ok) {
        logger.warn('USE_GAME_ACTIONS_ADD_BOT_FAILED', {
          gameId: gameState.id,
          profile,
          attemptedName: proposedName,
          alreadyCreated: created,
        });
        break;
      }
      created += 1;
    }

    return created > 0;
  }, [gameState.id, gameState.lobby?.phase, gameState.mode, gameState.players]);

  const removeBotPlayer = React.useCallback(async (botId: string): Promise<boolean> => {
    if (gameState.mode !== 'ONLINE' || !gameState.id) return false;
    if (!botId) return false;
    if (gameState.lobby?.phase !== 'LOBBY') return false;
    const targetBot = gameState.players.find((player) => player.id === botId);
    if (!targetBot) return true;
    if (targetBot.isBot !== true) return false;
    if ((targetBot.status || 'active') !== 'active') return true;
    const hostId = getSessionPlayerId();
    if (!hostId) return false;
    const ok = await sendOnlineCommand(gameState.id, {
      type: 'REMOVE_BOT',
      eventId: randomId(),
      hostId,
      botId,
    });
    if (!ok) {
      logger.warn('USE_GAME_ACTIONS_REMOVE_BOT_FAILED', {
        gameId: gameState.id,
        botId,
      });
    }
    return ok;
  }, [gameState.id, gameState.lobby?.phase, gameState.mode, gameState.players]);

  const markSelfPlayer = React.useCallback((playerId: string | null) => {
    if (gameState.mode !== 'REAL' && modeHint !== 'REAL') return;
    dispatch({ type: 'MARK_SELF_PLAYER', playerId });
  }, [dispatch, gameState.mode, modeHint]);

  const reorderPlayers = React.useCallback((players: Player[]) => {
    dispatch({ type: 'REORDER_PLAYERS', players });
  }, [dispatch]);

  const startGame = React.useCallback((id?: string) => {
    try {
      const currentId = gameState.id || localStorage.getItem(GAME_ID_KEY) || undefined;
      clearPerGameKeys(currentId || undefined);
      localStorage.setItem('dice1k-mode', modeHint || 'REAL');
    } catch {}
    try { clearPersistedEventCaches(); } catch {}
    // RNG zostaje poza reducerem: losujemy tutaj, a wynik jedzie jako dane w akcji.
    const zones = gameState.settings.zones
      ? (readMockZones() ?? placeZones(Math.random, gameState.settings.zonesAvoidTunnels === true))
      : undefined;
    dispatch({ type: 'START_GAME', gameId: id, mode: modeHint || 'REAL', zones });
  }, [dispatch, gameState.id, gameState.settings.zones, gameState.settings.zonesAvoidTunnels, modeHint]);

  const addScore = React.useCallback((playerId: string, points: number, turnOverride?: number) => {
    const now = Date.now();
    const turn = turnOverride ?? gameState.turnNumber;
    const prev = lastScoreActionRef.current;
    if (prev && prev.playerId === playerId && prev.points === points && prev.turn === turn && (now - prev.time) < 120) return;
    lastScoreActionRef.current = { playerId, points, turn, time: now };
    dispatch({ type: 'ADD_SCORE', playerId, points, turn });
  }, [dispatch, gameState.turnNumber, lastScoreActionRef]);

  const undoLastAction = React.useCallback(() => {
    const lastHistory = gameState.history[gameState.history.length - 1];
    if (lastHistory && gameState.mode === 'REAL' && gameState.id) {
      const delta = -lastHistory.points;
      const resulting = lastHistory.newScore + delta;
      const undoEvent = makePenaltyEvent(lastHistory.turn, lastHistory.playerId, delta, 'UNDO', resulting);
      void eventStore.appendEvent(gameState.id, undoEvent);
    }
    dispatch({ type: 'UNDO_LAST_ACTION' });
  }, [dispatch, gameState.history, gameState.id, gameState.mode]);

  const resetGame = React.useCallback(() => {
    try { clearGameState(); } catch {}
    try { hardResetStorage(); } catch {}
    try { clearAllDiceKeys(); } catch {}
    try {
      const currentId = gameState.id || localStorage.getItem(GAME_ID_KEY) || undefined;
      clearPerGameKeys(currentId || undefined);
      localStorage.setItem('dice1k-mode', modeHint || 'REAL');
      writeSession({
        version: 1,
        mode: modeHint || 'REAL',
        gameId: '',
        role: 'host',
        phase: 'home',
        lastPath: '/',
        updatedAt: new Date().toISOString(),
      });
    } catch {}
    try { clearPersistedEventCaches(); } catch {}
    dispatch({ type: 'RESET_GAME', mode: modeHint });
    setIsHost(true);
    clearReservedPlayerId();
    clearSessionPlayerId();
    setCurrentClientPlayerId(null);
  }, [dispatch, setCurrentClientPlayerId, setIsHost, gameState.id, modeHint]);

  const exitGame = React.useCallback(() => {
    try { clearGameState(); } catch {}
    try { hardResetStorage(); clearAllDiceKeys(); clearSession(); } catch {}
    try {
      const currentId = gameState.id || localStorage.getItem(GAME_ID_KEY) || undefined;
      clearPerGameKeys(currentId || undefined);
      clearPersistedEventCaches();
    } catch {}
    dispatch({ type: 'RESET_GAME', mode: modeHint });
    setIsHost(true);
    clearReservedPlayerId();
    clearSessionPlayerId();
    setCurrentClientPlayerId(null);
  }, [dispatch, setCurrentClientPlayerId, setIsHost, gameState.id, modeHint]);

  const startNextGame = React.useCallback(async (): Promise<string | null> => {
    if (gameState.mode === 'REAL') return null;
    const roomId = (gameState.id || '').trim().toUpperCase();
    if (!roomId) return null;
    const currentIdentity = resolveOnlineIdentity({
      gameId: gameState.currentRoundId || gameState.id,
      roomId: gameState.roomId || gameState.groupToken,
      currentRoundId: gameState.currentRoundId || gameState.id,
      groupToken: gameState.groupToken,
    });
    debug('startNextGame:begin', {
      ...currentIdentity,
      currentGameId: gameState.id,
      groupToken: gameState.groupToken || null,
      playersCount: gameState.players.length,
      spectatorsCount: (gameState.spectators || []).length,
      eventsCount: gameState.events?.length || 0,
    });
    debug('flow:next-round-start-begin', {
      ...currentIdentity,
      playersCount: gameState.players.length,
      spectatorsCount: (gameState.spectators || []).length,
      eventsCount: gameState.events?.length || 0,
    });
    const hostParticipantId = gameState.players.find((player) => player.isHost)?.id || getSessionPlayerId() || null;
    const nextGameParticipation = normalizeNextGameParticipation(gameState.lobby?.nextGameParticipation);
    const participantMap = new Map<string, {
      id: string;
      name: string;
      role: 'player' | 'viewer';
      status: 'active' | 'left';
      isHost: boolean;
      isBot?: boolean;
      botProfile?: BotProfile;
      color?: string;
    }>();

    (gameState.players || []).forEach((player) => {
      participantMap.set(player.id, {
        id: player.id,
        name: player.name,
        role: player.role === 'viewer' ? 'viewer' : 'player',
        status: player.status || 'active',
        isHost: player.isHost === true || player.id === hostParticipantId,
        isBot: player.isBot === true,
        botProfile: player.botProfile,
        color: player.color,
      });
    });

    (gameState.spectators || []).forEach((spectator) => {
      if (participantMap.has(spectator.id)) return;
      participantMap.set(spectator.id, {
        id: spectator.id,
        name: spectator.name,
        role: 'viewer',
        status: 'active',
        isHost: spectator.id === hostParticipantId,
      });
    });

    const activeParticipants = [...participantMap.values()].filter((participant) => participant.status === 'active');
    const selectedPlayers = activeParticipants.filter((participant) => participant.isBot === true || nextGameParticipation[participant.id] === true);
    const selectedPlayerIds = new Set(selectedPlayers.map((participant) => participant.id));
    const selectedViewers = activeParticipants.filter((participant) => !selectedPlayerIds.has(participant.id));

    if (selectedPlayers.length < 2) {
      debug('startNextGame:skip-no-candidates', { currentGameId: gameState.id });
      return null;
    }
    const inferredGroupToken = gameState.roomId
      ?? gameState.groupToken
      ?? parseGroupGameId(gameState.id || '')?.groupId
      ?? loadGroupToken();
    const currentRoundId = (gameState.currentRoundId || gameState.id || '').trim().toUpperCase();
    const nextRoundId = inferredGroupToken
      ? nextGroupGameId(inferredGroupToken, currentRoundId)
      : generateGameId();
    const nextIdentity = resolveOnlineIdentity({
      gameId: roomId,
      roomId: inferredGroupToken || currentIdentity.roomId || null,
      currentRoundId: nextRoundId,
      groupToken: inferredGroupToken || null,
    });
    debug('startNextGame:next-round-id', {
      ...nextIdentity,
      currentGameId: gameState.id,
      roomId,
      nextRoundId,
      inferredGroupToken: inferredGroupToken || null,
      carriedPlayers: selectedPlayers.length,
      carriedViewers: selectedViewers.length,
    });
    const expectedParticipantIds = new Set(activeParticipants.map((participant) => participant.id));
    const nextHostId = hostParticipantId || activeParticipants.find((participant) => participant.isHost)?.id || activeParticipants[0]?.id || null;
    const previousRoomId = roomId;
    try {
      const restartEvent = makeGameRestartedEvent(roomId, { currentRoundId: nextRoundId });
      const restartResult = await eventStore.appendEvent(roomId, restartEvent);
      if (!restartResult.success) {
        logger.warn('USE_GAME_ACTIONS_RESTART_BROADCAST_FAILED', { message: restartResult.error });
        debug('startNextGame:broadcast-failed', {
          currentGameId: previousRoomId,
          roomId,
          nextRoundId,
          error: restartResult.error || null,
        });
        return null;
      }

      if (inferredGroupToken) {
        rememberGroupContext(inferredGroupToken, roomId);
      }
      setIsHost(true);
      if (nextHostId) {
        setCurrentClientPlayerId(nextHostId);
        setSessionPlayerId(nextHostId);
        if (inferredGroupToken) {
          const nextHostPlayer = activeParticipants.find((participant) => participant.id === nextHostId);
          if (nextHostPlayer) {
            rememberGroupPlayer(inferredGroupToken, nextHostPlayer.id, { isHost: true });
          }
        }
        try { localStorage.setItem(`dice1k_last_player_id_${roomId}`, nextHostId); } catch {}
      }
      try { localStorage.setItem('dice1k-mode', 'ONLINE'); } catch {}

      debug('startNextGame:done', {
        ...nextIdentity,
        currentGameId: previousRoomId,
        roomId,
        nextRoundId,
        playersSeeded: expectedParticipantIds.size,
        remoteHydrated: false,
        hydrationStrategy: 'live-transport',
      });
      debug('flow:next-round-start-ok', {
        ...nextIdentity,
        previousRoundId: currentIdentity.currentRoundId,
        playersSeeded: expectedParticipantIds.size,
      });

      return nextRoundId;
    } catch (e) {
      logger.warn('USE_GAME_ACTIONS_RESTART_EXCEPTION', {
        currentGameId: previousRoomId,
        roomId,
        nextRoundId,
        error: getErrorMessage(e),
      });
      debug('flow:next-round-start-failed', {
        ...currentIdentity,
        nextRoundId,
        error: getErrorMessage(e),
      });
      return null;
    }
  }, [gameState.currentRoundId, gameState.events, gameState.groupToken, gameState.id, gameState.lobby?.nextGameParticipation, gameState.mode, gameState.players, gameState.roomId, gameState.spectators, setCurrentClientPlayerId, setIsHost]);

  const leaveGame = React.useCallback(async (): Promise<boolean> => {
    if (gameState.mode === 'REAL') return false;
    if (!gameState.lobby || gameState.lobby.phase !== 'IN_GAME') return false;
    const playerId = getSessionPlayerId();
    if (!playerId) return false;
    const player = gameState.players.find(p => p.id === playerId);
    if (!player || (player.status && player.status !== 'active')) return false;
    const eventId = randomId();
    const ev: PlayerLeftGameEvent = {
      id: eventId,
      type: 'PLAYER_LEFT',
      turn: gameState.turnNumber,
      timestamp: new Date().toISOString(),
      payload: { playerId, eventId },
    };
    const result = await eventStore.appendEvent(gameState.id, ev);
    if (!result?.success) {
      logger.error('USE_GAME_ACTIONS_LEAVE_FAILED', {
        gameId: gameState.id,
        eventId: ev.id,
        error: result?.error,
        warning: result?.warning,
      });
      return false;
    }
    setCurrentClientPlayerId(playerId);
    setSessionPlayerId(playerId);
    return true;
  }, [gameState.id, gameState.lobby, gameState.mode, gameState.players, gameState.turnNumber, setCurrentClientPlayerId]);

  const joinGame = React.useCallback(async (gameId: string, options?: { groupToken?: string | null; skipGroupLookup?: boolean; assumeHost?: boolean }): Promise<boolean> => {
    const normId = gameId.trim().toUpperCase();
    const targetMode = modeHint || 'ONLINE';
    const preferredGroupToken = options?.groupToken || null;
    const sessionPlayerId = getSessionPlayerId();
    const joinIdentity = resolveOnlineIdentity({
      gameId: normId,
      currentRoundId: normId,
      groupToken: preferredGroupToken,
    });
    debug('joinGame:start', { id: normId, mode: targetMode, preferredGroupToken: preferredGroupToken?.slice(0, 6), ...joinIdentity });
    debug('flow:join-room-begin', { ...joinIdentity, mode: targetMode });
    try {
      perf.mark('joinGame');
      if (targetMode === 'ONLINE' && !sessionPlayerId && !getReservedPlayerId(normId)) {
        setReservedPlayerId(normId, randomId());
      }
      if (targetMode === 'ONLINE') {
        const partyKitLobby = await requestLobbyState(normId, {
          playerId: sessionPlayerId,
          playerName: null,
          role: null,
        });
        if (partyKitLobby && partyKitLobby.gameId === normId) {
          let group: import('../../services/GroupService').GroupRecord | null = null;
          if (!options?.skipGroupLookup) {
            try {
              group = await findGroupByGame(normId);
            } catch (err) {
              logger.warn('JOIN_GAME_GROUP_LOOKUP_FAILED', { message: getErrorMessage(err) });
            }
          }
          const parsedGroupToken = parseGroupGameId(normId)?.groupId || null;
          const nextGroupToken = group?.token || preferredGroupToken || parsedGroupToken || null;
          const nextGroupId = group?.id || null;
          dispatch({
            type: 'LOAD_GAME',
            gameState: {
              ...initialState,
              id: normId,
              mode: 'ONLINE',
              lobby: { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} },
              history: [],
              events: [],
              groupToken: nextGroupToken,
              groupId: nextGroupId,
            },
          });
          dispatch({ type: 'HYDRATE_LOBBY_STATE', snapshot: partyKitLobby });
          if (typeof options?.assumeHost === 'boolean') {
            setIsHost(options.assumeHost);
          }
          try { localStorage.setItem('dice1k-mode', targetMode); } catch {}
          if (nextGroupToken) {
            rememberGroupContext(nextGroupToken, normId);
            void attachGameToGroup(nextGroupToken, normId).catch((err) => {
              logger.warn('JOIN_GAME_GROUP_ATTACH_FAILED', { message: getErrorMessage(err) });
            });
          }
          debug('joinGame:successPartykitLobby', {
            id: normId,
            players: partyKitLobby.players.length,
            phase: partyKitLobby.phase,
          });
          debug('flow:join-room-ok', {
            ...joinIdentity,
            phase: partyKitLobby.phase,
            players: partyKitLobby.players.length,
            source: 'partykit-lobby',
          });
          perf.end('joinGame');
          return true;
        }
      }
      const t0 = performance.now();
      const reconstructedState = await eventStore.getGameState(normId);
      debug('joinGame:stateFetched', { id: normId, players: reconstructedState.players.length, ms: Math.round(performance.now() - t0) });
      if (!reconstructedState || reconstructedState.mode !== 'ONLINE') {
        debug('joinGame:failNoState', { id: normId });
        debug('flow:join-room-failed', { ...joinIdentity, reason: 'NO_ONLINE_STATE' });
        return false;
      }
      const inProgress = reconstructedState.lobby?.phase === 'IN_GAME' || Boolean(reconstructedState.turnCycle);
      if (!inProgress && reconstructedState.players.length >= PLAYER_MAX_COUNT) {
        debug('joinGame:full', { id: normId });
        debug('flow:join-room-failed', { ...joinIdentity, reason: 'ROOM_FULL', players: reconstructedState.players.length });
        return false;
      }

      let group: import('../../services/GroupService').GroupRecord | null = null;
      if (!options?.skipGroupLookup) {
        try {
          group = await findGroupByGame(normId);
        } catch (err) {
          logger.warn('JOIN_GAME_GROUP_LOOKUP_FAILED', { message: getErrorMessage(err) });
        }
      }

      const parsedGroupToken = parseGroupGameId(normId)?.groupId || null;
      const nextGroupToken = group?.token || preferredGroupToken || parsedGroupToken || null;
      const nextGroupId = group?.id || null;

      dispatch({ type: 'LOAD_GAME', gameState: { ...reconstructedState, history: [], groupToken: nextGroupToken, groupId: nextGroupId } });
      if (typeof options?.assumeHost === 'boolean') {
        setIsHost(options.assumeHost);
      }
      try { localStorage.setItem('dice1k-mode', targetMode); } catch {}
      if (nextGroupToken) {
        rememberGroupContext(nextGroupToken, normId);
        void attachGameToGroup(nextGroupToken, normId).catch((err) => {
          logger.warn('JOIN_GAME_GROUP_ATTACH_FAILED', { message: getErrorMessage(err) });
        });
      }
      debug('joinGame:success', { id: normId });
      debug('flow:join-room-ok', {
        ...joinIdentity,
        phase: reconstructedState.lobby?.phase || null,
        players: reconstructedState.players.length,
        source: 'event-store',
      });
      perf.end('joinGame');
      return true;
    } catch (e) {
      debug('joinGame:error', { id: normId, message: getErrorMessage(e) });
      debug('flow:join-room-failed', { ...joinIdentity, reason: 'EXCEPTION', error: getErrorMessage(e) });
      perf.end('joinGame');
      return false;
    }
  }, [dispatch, modeHint, setIsHost]);

  return {
    addPlayer,
    addPlayerRemote,
    addOnlinePlayer,
    joinOnlineSelf,
    addBotPlayers,
    removeBotPlayer,
    markSelfPlayer,
    reorderPlayers,
    startGame,
    startNextGame,
    addScore,
    undoLastAction,
    resetGame,
    exitGame,
    joinGame,
    leaveGame,
  };
}
